import { useEffect, useRef, useState } from 'react'
import { sdkErrorMessage } from './sdk-client'

export type ListState<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: T[] }

// SDK-call replacement for `use-list.ts`'s path-based `useList<T>(path)` --
// takes a zero-arg fetcher (typically an SDK client call wrapped in
// useCallback by the caller) instead of a URL path.
export function useSdkList<T>(fetcher: () => Promise<{ data: T[] }>): ListState<T> & { reload: () => void } {
  const [state, setState] = useState<ListState<T>>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)
  const hasReadyData = useRef(false)

  // A genuinely new list (a different `fetcher`, e.g. a different account
  // slug) must show its own loading state, not silently reuse the previous
  // list's "already have ready data" flag and skip straight to a background
  // refresh.
  useEffect(() => {
    hasReadyData.current = false
  }, [fetcher])

  useEffect(() => {
    let cancelled = false
    // Only show the loading skeleton for the initial fetch -- a reload() after
    // dismiss/merge should refresh in the background, not flash the table back
    // to "loading" (defeats the optimistic removal) or discard already-good
    // data on a transient failure (a 5xx/timeout on the background refresh
    // shouldn't turn a successful action into a full-page error).
    if (!hasReadyData.current) setState({ status: 'loading' })
    fetcher()
      .then((body) => {
        if (cancelled) return
        hasReadyData.current = true
        setState({ status: 'ready', data: body.data })
      })
      .catch((error: unknown) => {
        if (cancelled || hasReadyData.current) return
        setState({ status: 'error', message: sdkErrorMessage(error, 'Failed to load list.') })
      })
    return () => {
      cancelled = true
    }
  }, [fetcher, reloadKey])

  return { ...state, reload: () => setReloadKey((k) => k + 1) }
}
